import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import type { RootState, AppDispatch } from '../index';
import {
  fetchReminderCalendar,
  setFilters,
  clearFilters,
  setLoading,
  setEvents,
  selectReminderCalendar,
  selectReminderCalendarEvents,
  selectReminderCalendarFilters,
  selectReminderCalendarLoading,
  selectReminderCalendarError,
  type ReminderCalendarFilters,
} from './slice';

// Hook for using reminder calendar redux functionality
export const useReminderCalendarRedux = () => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Selectors
  const reminderCalendar = useSelector((state: RootState) => selectReminderCalendar(state));
  const events = useSelector((state: RootState) => selectReminderCalendarEvents(state));
  const filters = useSelector((state: RootState) => selectReminderCalendarFilters(state));
  const loading = useSelector((state: RootState) => selectReminderCalendarLoading(state));
  const error = useSelector((state: RootState) => selectReminderCalendarError(state));

  // Actions
  const updateFilters = useCallback((newFilters: Partial<ReminderCalendarFilters>) => {
    dispatch(setFilters(newFilters));
  }, [dispatch]);

  const fetchWithFilters = useCallback((newFilters?: Partial<ReminderCalendarFilters>) => {
    const filtersToUse = newFilters ? { ...filters, ...newFilters } : filters;
    dispatch(fetchReminderCalendar(filtersToUse));
  }, [dispatch, filters]);

  const clearAllFilters = useCallback((useInitial = false) => {
    dispatch(clearFilters({ useInitial }));
  }, [dispatch]);

  const setLoadingState = useCallback((isLoading: boolean) => {
    dispatch(setLoading(isLoading));
  }, [dispatch]);

  const setEventsData = useCallback((eventsData: any[]) => {
    dispatch(setEvents(eventsData));
  }, [dispatch]);

  // Effect to fetch data when filters change (debounced)
  useEffect(() => {
    // Only fetch if we have at least date filters set
    if (filters.startDate && filters.endDate) {
      const timeoutId = setTimeout(() => {
        dispatch(fetchReminderCalendar(filters));
      }, 300); // 300ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [filters, dispatch]);

  return {
    // State
    reminderCalendar,
    events,
    filters,
    loading,
    error,
    
    // Actions
    updateFilters,
    fetchWithFilters,
    clearAllFilters,
    setLoadingState,
    setEventsData,
  };
};

export default useReminderCalendarRedux;